import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { TeacherCommentsDialog } from "../questionnaires/TeacherCommentsDialog";
import {
  getQuestionnaireDashboardReports,
  getQuestionnaireExportReport,
  listQuestionnaires,
} from "../../api/endpoints/questionnaires";
import { getAllClassrooms } from "../../api/sponteAPI";
import { downloadQuestionnaireExcel } from "../../utils/questionnaireExcelExport";

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

export function Questionnaires() {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState("");
  const [classrooms, setClassrooms] = useState([]);
  const [selectedTurmaId, setSelectedTurmaId] = useState("");
  const [tab, setTab] = useState(0);
  const [completion, setCompletion] = useState(null);
  const [summary, setSummary] = useState(null);
  const [textAnswers, setTextAnswers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");
  const [loadError, setLoadError] = useState("");
  const [commentsTeacher, setCommentsTeacher] = useState(null);

  const observationsByTeacher = useMemo(() => {
    const map = new Map();
    for (const item of textAnswers?.items || []) {
      if (!item.teacher_id) continue;
      const key = String(item.teacher_id);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    }
    return map;
  }, [textAnswers]);

  useEffect(() => {
    listQuestionnaires()
      .then((res) => {
        const items = res.questionnaires || [];
        setQuestionnaires(items);
        setLoadError(
          items.length === 0
            ? "Nenhum questionário cadastrado. Execute a migration do backend (create-questionnaires) no banco desta unidade."
            : ""
        );
        if (items.length) {
          setSelectedQuestionnaireId(String(items[0].id));
        }
      })
      .catch((err) => {
        setQuestionnaires([]);
        setLoadError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Não foi possível carregar os questionários. Verifique se o backend foi atualizado e a migration foi executada."
        );
      });
  }, []);

  const selectedSchoolYear =
    questionnaires.find((q) => String(q.id) === String(selectedQuestionnaireId))
      ?.school_year || new Date().getFullYear().toString();

  useEffect(() => {
    getAllClassrooms(selectedSchoolYear).then((res) => {
      const list = Array.isArray(res) ? res : [];
      setClassrooms(list);
      if (list.length > 0) {
        setSelectedTurmaId(String(list[0].TurmaID));
      }
    });
  }, [selectedSchoolYear]);

  const loadReports = useCallback(async () => {
    if (!selectedQuestionnaireId) return;
    setLoading(true);
    try {
      const turma = selectedTurmaId || null;
      const dashboard = await getQuestionnaireDashboardReports(
        selectedQuestionnaireId,
        turma
      );
      setCompletion(dashboard.completion);
      setSummary(dashboard.summary);
      setTextAnswers(dashboard.textAnswers);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [selectedQuestionnaireId, selectedTurmaId]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const pendingStudents =
    completion?.students?.filter((s) => s.status === "PENDING") || [];

  const selectedClassroomName =
    classrooms.find((c) => String(c.TurmaID) === String(selectedTurmaId))?.Nome ||
    selectedTurmaId;

  const handleExport = async (scope) => {
    if (!selectedQuestionnaireId) return;
    setExporting(true);
    setExportMessage("");
    try {
      const turmaId = scope === "classroom" ? selectedTurmaId : null;
      const data = await getQuestionnaireExportReport(
        selectedQuestionnaireId,
        turmaId
      );

      if (!data.rows || data.rows.length === 0) {
        setExportMessage(
          scope === "classroom"
            ? "Nenhuma resposta encontrada para a turma selecionada."
            : "Nenhuma resposta encontrada em nenhuma turma."
        );
        return;
      }

      const suffix =
        scope === "classroom"
          ? `turma_${sanitizeSlug(selectedClassroomName)}`
          : "todas_turmas";

      downloadQuestionnaireExcel(data, suffix);
      setExportMessage(
        `Excel gerado com ${data.rows.length} resposta(s).`
      );
    } catch (err) {
      setExportMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Erro ao exportar respostas para Excel."
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      {loadError && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {loadError}
        </Typography>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel>Questionário</InputLabel>
          <Select
            label="Questionário"
            value={selectedQuestionnaireId}
            onChange={(e) => setSelectedQuestionnaireId(e.target.value)}
          >
            {questionnaires.map((q) => (
              <MenuItem key={q.id} value={String(q.id)}>
                {q.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Turma</InputLabel>
          <Select
            label="Turma"
            value={selectedTurmaId}
            onChange={(e) => setSelectedTurmaId(e.target.value)}
          >
            {classrooms.map((c) => (
              <MenuItem key={String(c.TurmaID)} value={String(c.TurmaID)}>
                {c.Nome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          color="success"
          size="small"
          startIcon={<DownloadIcon />}
          disabled={!selectedQuestionnaireId || !selectedTurmaId || exporting}
          onClick={() => handleExport("classroom")}
          sx={{ alignSelf: "center" }}
        >
          Excel – Turma
        </Button>

        <Button
          variant="outlined"
          color="success"
          size="small"
          startIcon={<DownloadIcon />}
          disabled={!selectedQuestionnaireId || exporting}
          onClick={() => handleExport("all")}
          sx={{ alignSelf: "center" }}
        >
          Excel – Todas as turmas
        </Button>
      </Box>

      {exportMessage && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {exportMessage}
        </Typography>
      )}

      {loading && <Typography variant="body2">Carregando relatórios...</Typography>}

      {completion && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
          <Chip label={`Total: ${completion.total_students}`} />
          <Chip color="success" label={`Responderam: ${completion.responded_count}`} />
          <Chip color="warning" label={`Pendentes: ${completion.pending_count}`} />
          <Chip label={`Conclusão: ${completion.completion_rate}%`} />
          <Chip
            variant="outlined"
            label={completion.classroom_enabled ? "Turma ativa" : "Turma inativa"}
          />
        </Box>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Quem falta" />
        <Tab label="Médias" />
        <Tab label="Respostas abertas" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <Paper sx={{ overflow: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Aluno</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingStudents.map((s) => (
                <TableRow key={s.aluno_id}>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>
                    <Chip size="small" color="warning" label="Pendente" />
                  </TableCell>
                </TableRow>
              ))}
              {pendingStudents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2}>Nenhum aluno pendente nesta turma.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        {summary?.summary?.mode === "per_teacher" ? (
          (summary.summary.teachers || []).map((teacher) => {
            const teacherComments =
              observationsByTeacher.get(String(teacher.teacherId)) || [];
            return (
            <Paper key={teacher.teacherId} sx={{ overflow: "auto", mb: 2, p: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                  {teacher.teacherName}
                  {teacher.subjects ? ` — ${teacher.subjects}` : ""}
                </Typography>
                <Tooltip title="Ver observações dos alunos">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setCommentsTeacher(teacher)}
                    aria-label={`Observações de ${teacher.teacherName}`}
                  >
                    <Badge
                      badgeContent={teacherComments.length}
                      color="secondary"
                      invisible={teacherComments.length === 0}
                    >
                      <ChatBubbleOutlineIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Critério</TableCell>
                    <TableCell>Média</TableCell>
                    <TableCell>Respostas</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(teacher.criteria || []).map((row) => (
                    <TableRow key={`${teacher.teacherId}-${row.questionId}`}>
                      <TableCell>{row.text}</TableCell>
                      <TableCell>{row.average ?? "-"}</TableCell>
                      <TableCell>{row.responseCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
            );
          })
        ) : (
          <Paper sx={{ overflow: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Pergunta</TableCell>
                  <TableCell>Média</TableCell>
                  <TableCell>Respostas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(Array.isArray(summary?.summary) ? summary.summary : []).map((row) => (
                  <TableRow key={row.questionId}>
                    <TableCell>{row.text}</TableCell>
                    <TableCell>{row.average ?? "-"}</TableCell>
                    <TableCell>{row.responseCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <Paper sx={{ p: 2 }}>
          {(textAnswers?.items || []).map((item, idx) => (
            <Box key={`${item.questionId}-${idx}`} sx={{ mb: 2 }}>
              <Typography variant="subtitle2">{item.questionText}</Typography>
              <Typography variant="caption" color="text.secondary">
                {item.teacher_name
                  ? `${item.teacher_name} · ${item.student_name || "Aluno"}`
                  : item.student_name || "Aluno"}
              </Typography>
              <Typography variant="body2">{item.answer}</Typography>
            </Box>
          ))}
          {(textAnswers?.items || []).length === 0 && (
            <Typography variant="body2">Nenhuma resposta aberta encontrada.</Typography>
          )}
        </Paper>
      </TabPanel>

      <TeacherCommentsDialog
        open={Boolean(commentsTeacher)}
        onClose={() => setCommentsTeacher(null)}
        teacher={commentsTeacher}
        comments={
          commentsTeacher
            ? observationsByTeacher.get(String(commentsTeacher.teacherId)) || []
            : []
        }
      />
    </Box>
  );
}

function sanitizeSlug(value) {
  return String(value || "turma")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 40);
}
